/**
 * Market Panel - News banner, price chart, manipulation buttons
 * Integrated into the main screen
 */
import { MARKET_CONFIG, MANIPULATIONS } from '../game/config/market.js';
import { getCurrentCPS } from '../game/state.js';
import { formatNumber } from '../game/utils.js';
import { t } from '../i18n/index.js';
import { playSfx } from '../sound.js';
import { getState, dispatch, requestRender, markScreenDirty } from './renderer.js';

let wrapper: HTMLDivElement | null = null;
let newsEl: HTMLDivElement | null = null;
let chartCanvas: HTMLCanvasElement | null = null;
let chartCtx: CanvasRenderingContext2D | null = null;
let manipRow: HTMLDivElement | null = null;

export function createMarketPanel(): HTMLElement {
  wrapper = document.createElement('div');
  wrapper.id = 'market-panel';
  wrapper.className = 'market-panel';

  // News banner
  newsEl = document.createElement('div');
  newsEl.id = 'market-news';
  newsEl.className = 'market-news';
  newsEl.style.display = 'none';
  wrapper.appendChild(newsEl);

  // Price chart
  const chartWrap = document.createElement('div');
  chartWrap.className = 'market-chart-wrap';
  chartCanvas = document.createElement('canvas');
  chartCanvas.className = 'market-chart-canvas';
  chartCanvas.width = 320;
  chartCanvas.height = 60;
  chartWrap.appendChild(chartCanvas);
  wrapper.appendChild(chartWrap);
  chartCtx = chartCanvas.getContext('2d');

  // Manipulation buttons
  manipRow = document.createElement('div');
  manipRow.className = 'market-manip-row';
  wrapper.appendChild(manipRow);

  // Event delegation for manipulation buttons
  manipRow.addEventListener('click', onManipClick);

  return wrapper;
}

function onManipClick(e: Event) {
  const target = (e.target as HTMLElement).closest('[data-manip]') as HTMLElement | null;
  if (!target) return;
  const manipId = target.dataset.manip!;
  dispatch({ type: 'MARKET_MANIPULATE', payload: { manipulationId: manipId } });
  const st = getState();
  if (st.market?.activeManipulation?.id === manipId) {
    playSfx('purchase');
  } else {
    playSfx('error');
  }
  markScreenDirty();
  requestRender();
}

export function updateMarketPanel() {
  const st = getState();
  if (!st.market) return;
  const now = Date.now();

  // === News banner ===
  if (newsEl) {
    if (st.market.activeNews && now < st.market.activeNews.expiresAt) {
      const rem = Math.ceil((st.market.activeNews.expiresAt - now) / 1000);
      const text = t(st.market.activeNews.id) || st.market.activeNews.id;
      newsEl.innerHTML = `<span class="market-news-text">${text}</span><span class="market-news-timer">${rem}s</span>`;
      newsEl.style.display = 'flex';

      // Special event indicator
      if (st.market.specialEvent) {
        newsEl.classList.toggle('market-news-crash', st.market.specialEvent.type === 'crash');
        newsEl.classList.toggle('market-news-boom', st.market.specialEvent.type === 'boom');
      } else {
        newsEl.classList.remove('market-news-crash', 'market-news-boom');
      }
    } else {
      newsEl.style.display = 'none';
    }
  }

  // === Chart ===
  drawChart(st.market.priceHistory, st.market.price);

  // === Manipulation buttons ===
  if (manipRow) {
    const cps = getCurrentCPS(st);
    let html = '';
    for (const m of MANIPULATIONS) {
      const cost = Math.max(m.minCost, cps * m.costMultiplier);
      const cdUntil = st.market.manipulationCooldowns[m.id] || 0;
      const onCooldown = now < cdUntil;
      const isActive = st.market.activeManipulation && now < st.market.activeManipulation.expiresAt;
      const canAfford = st.coins >= cost;
      const disabled = onCooldown || !!isActive || !canAfford;

      let label = t('manip_' + m.id) || m.id;
      let sublabel = '';

      if (onCooldown) {
        const cdRem = Math.ceil((cdUntil - now) / 1000);
        sublabel = `${cdRem}s`;
      } else if (isActive && st.market.activeManipulation?.id === m.id) {
        const rem = Math.ceil((st.market.activeManipulation.expiresAt - now) / 1000);
        sublabel = `${rem}s`;
      } else {
        sublabel = formatNumber(cost);
      }

      const cls = disabled ? 'market-manip-btn market-manip-disabled' : 'market-manip-btn';
      html += `<button class="${cls}" data-manip="${m.id}">
        <span class="manip-label">${label}</span>
        <span class="manip-cost">${sublabel}</span>
      </button>`;
    }
    manipRow.innerHTML = html;
  }
}

function drawChart(priceHistory: number[], currentPrice: number) {
  if (!chartCtx || !chartCanvas) return;
  const dpr = window.devicePixelRatio || 1;
  const displayW = chartCanvas.clientWidth || chartCanvas.width;
  const displayH = chartCanvas.clientHeight || chartCanvas.height;

  if (chartCanvas.width !== displayW * dpr || chartCanvas.height !== displayH * dpr) {
    chartCanvas.width = displayW * dpr;
    chartCanvas.height = displayH * dpr;
    chartCtx.scale(dpr, dpr);
  }

  const cw = displayW;
  const ch = displayH;
  chartCtx.clearRect(0, 0, cw, ch);

  if (priceHistory.length < 2) return;

  const { minPrice, maxPrice } = MARKET_CONFIG;
  const padTop = 14;
  const padBottom = 2;
  const padLeft = 2;
  const padRight = 2;
  const graphW = cw - padLeft - padRight;
  const graphH = ch - padTop - padBottom;
  const maxPts = MARKET_CONFIG.priceHistoryLength;

  // Determine color based on trend
  const isUp = priceHistory.length >= 2 && priceHistory[priceHistory.length - 1] >= priceHistory[priceHistory.length - 2];
  const lineColor = isUp ? '#00FF88' : '#FF3D3D';
  const fillColorTop = isUp ? 'rgba(0, 255, 136, 0.25)' : 'rgba(255, 61, 61, 0.2)';
  const fillColorBot = isUp ? 'rgba(0, 255, 136, 0.0)' : 'rgba(255, 61, 61, 0.0)';

  // Gradient fill
  const gradient = chartCtx.createLinearGradient(0, padTop, 0, ch);
  gradient.addColorStop(0, fillColorTop);
  gradient.addColorStop(1, fillColorBot);

  // Draw fill
  chartCtx.beginPath();
  for (let i = 0; i < priceHistory.length; i++) {
    const x = padLeft + (i / (maxPts - 1)) * graphW;
    const normY = (priceHistory[i] - minPrice) / (maxPrice - minPrice);
    const y = padTop + graphH - normY * graphH;
    if (i === 0) chartCtx.moveTo(x, y);
    else chartCtx.lineTo(x, y);
  }
  const lastX = padLeft + ((priceHistory.length - 1) / (maxPts - 1)) * graphW;
  chartCtx.lineTo(lastX, ch);
  chartCtx.lineTo(padLeft, ch);
  chartCtx.closePath();
  chartCtx.fillStyle = gradient;
  chartCtx.fill();

  // Draw line
  chartCtx.beginPath();
  for (let i = 0; i < priceHistory.length; i++) {
    const x = padLeft + (i / (maxPts - 1)) * graphW;
    const normY = (priceHistory[i] - minPrice) / (maxPrice - minPrice);
    const y = padTop + graphH - normY * graphH;
    if (i === 0) chartCtx.moveTo(x, y);
    else chartCtx.lineTo(x, y);
  }
  chartCtx.strokeStyle = lineColor;
  chartCtx.lineWidth = 1.5;
  chartCtx.lineJoin = 'round';
  chartCtx.stroke();

  // Last point dot
  if (priceHistory.length > 0) {
    const lastVal = priceHistory[priceHistory.length - 1];
    const lx = padLeft + ((priceHistory.length - 1) / (maxPts - 1)) * graphW;
    const normY = (lastVal - minPrice) / (maxPrice - minPrice);
    const ly = padTop + graphH - normY * graphH;
    chartCtx.beginPath();
    chartCtx.arc(lx, ly, 3, 0, Math.PI * 2);
    chartCtx.fillStyle = lineColor;
    chartCtx.fill();
  }

  // Price label
  const mult = ((currentPrice - minPrice) / (maxPrice - minPrice)) * (MARKET_CONFIG.multiplierMax - MARKET_CONFIG.multiplierMin) + MARKET_CONFIG.multiplierMin;
  const multPct = Math.round(mult * 100);
  chartCtx.font = '600 10px Inter, sans-serif';
  chartCtx.textAlign = 'left';
  chartCtx.fillStyle = lineColor;
  chartCtx.fillText(`${t('market_price') || 'Market'}: ${Math.round(currentPrice)}`, padLeft + 2, 10);

  chartCtx.textAlign = 'right';
  chartCtx.fillStyle = mult >= 1 ? '#00FF88' : '#FF3D3D';
  chartCtx.fillText(`x${(mult).toFixed(2)} (${multPct}%)`, cw - padRight - 2, 10);
}
